import { AUTH_ACCESS_EXPIRY_COOKIE_NAME } from '@upright/common/iam/login/access-token-expiry-cookie';
import { AUTH_CLAIM_QUERY_PARAM, AUTH_CLAIM_QUERY_VALUE } from '@upright/common/iam/login/auth-claim-query-param';
import { getServerBaseUrl } from '@upright/ui/untidy/server-base-url';
import { logErrorIfBadRequest, logNetworkError } from './logging';
import { getRenderViewAccessToken } from './render-view-access-token';
import { ApiTarget } from './types';

class TokenExpiredError extends Error {}

/**
 * Which session this page's requests ride on. The platform's (`up-*` cookies) everywhere, except
 * the Arvo console on Eevo's hosts once Eevo has its own identity (PLT-7477): there the session is
 * Eevo's (`eevo-*` cookies, `upserve/modules/arvo/auth/`), and so is the refresh. Set once, by the
 * console, before its first request (`views/arvo/index.tsx`).
 */
let sessionKind: 'platform' | 'eevo' = 'platform';

export function switchRequestsToEevoSession(): void {
  sessionKind = 'eevo';
}

const EEVO_ACCESS_EXPIRY_COOKIE_NAME = 'eevo-access-expiry';

/**
 * The function used by all fetcher utilities
 * Don't use directly. Instead, use React Query and one of the following functions:
 * - fetchOrThrow
 * - fetchOrThrowNoReturnData
 * - fetchOrThrowBlob
 *
 * @param path
 * @param method
 * @param bodyContent
 * @param apiTarget
 * @param customHeaders
 * @returns Response
 */
// eslint-disable-next-line upright/too-many-arguments
export async function buildRequestAndFetch(
  path: string,
  method = 'GET',
  bodyContent?: any,
  apiTarget: ApiTarget = ApiTarget.SERVER,
  customHeaders: HeadersInit = {},
) {
  // Build the request
  const url = buildUrl(path, apiTarget);
  const body = buildBody(bodyContent);
  // In the headless render view the JWT arrives as an `?accessToken=` URL
  // parameter instead of a cookie — see `render-view-access-token.ts`
  // (PLT-5056). Server-side, `hapi-auth-jwt2` prefers this Bearer header
  // over the cookie. An explicit Authorization in `customHeaders` (spread
  // below) still overrides it.
  const renderViewToken = getRenderViewAccessToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(renderViewToken ? { Authorization: `Bearer ${renderViewToken}` } : {}),
    ...customHeaders,
  };

  // Internal API auth travels as the `up-access` HttpOnly cookie via
  // `credentials: 'include'` in `fetchAndLog` (PLT-4720). The JWT itself is
  // not readable from JavaScript, but the server maintains a JS-readable
  // `up-access-expiry` companion cookie precisely so we can refresh
  // *proactively*: if the access cookie is about to (or did) expire, renew
  // it before firing the request. This keeps requests from ever going out
  // with a lapsed cookie in the common case — important not only to avoid
  // 401 noise, but because `auth: { mode: 'optional' }` endpoints don't
  // 401 at all: they silently answer as anonymous (READ_TEASER), which the
  // retry logic below cannot detect (sporadic paywall / stuck-loading bug).
  // External `/v1` API consumers still authenticate via their own Bearer
  // header (handled outside this fetcher; see `fetchers/`).
  //
  // In the render view there is no refresh cookie to refresh and the token
  // in the URL is all we will ever have, so both the proactive refresh and
  // the 401-retry below are skipped — doomed `/api/refresh-access-token`
  // calls would only delay puppeteer's `networkidle0`.
  if (apiTarget === ApiTarget.SERVER && !renderViewToken) {
    await refreshAccessTokenIfExpiringSoon();
  }

  // Append the `?auth=1` cache-key claim AFTER the proactive refresh: the
  // refresh can be the very thing that (re)establishes the authenticated
  // state the claim describes.
  const requestUrl = withAuthClaim(url, method, apiTarget, renderViewToken !== null);

  const response = await fetchAndLog(requestUrl, method, headers, body);
  if (response.status !== 401 || apiTarget !== ApiTarget.SERVER || renderViewToken) {
    return response;
  }

  // Fallback: 401 on an internal API call — try to refresh the access
  // cookie and retry exactly once. With the proactive refresh above this
  // should be rare (clock skew, a cookie dropped by the browser, an access
  // token revoked server-side). We don't loop: a second 401 means the
  // refresh cookie is also expired / revoked. We deliberately do NOT
  // redirect the user to `/login` from here — for an anonymous caller (no
  // cookie yet, e.g. landing on `/sign-in/<code>`) the very first request
  // 401s and we would otherwise yank them away from the route that was
  // about to log them in. Instead we just return the 401 response and let
  // the caller (saga, route component) decide whether to force a re-login.
  // Long-running flows that previously relied on the global redirect call
  // `isAuthenticatedSession()` explicitly.
  try {
    await refreshAccessToken();
  } catch {
    return response;
  }
  return await fetchAndLog(requestUrl, method, headers, body);
}

/**
 * Append the auth-state cache-key claim (`?auth=1`, PLT-5219) to internal
 * GET requests when this client believes it is authenticated — i.e. the
 * JS-readable `up-access-expiry` companion cookie is present (it rides the
 * refresh cookie's TTL and is cleared on logout, so its presence means "this
 * browser is, or can refresh itself back to, an authenticated session").
 *
 * The claim's only job is to split cache keys: anonymous and authenticated
 * responses for the same path must not share an entry in the browser cache
 * or CloudFront, and the URL is the one part of the request every cache
 * keys on (auth itself rides in an HttpOnly cookie that no cache can vary
 * on). The server strips the parameter before routing and refuses to cache
 * any response that was served as anonymous despite the claim — so a stale
 * claim (cookie expired mid-session, refresh dead) can never poison the
 * authenticated key. See `auth-claim-query-param.ts` for the full design.
 *
 * GET-only: caches only store GETs, and the parameter carries no meaning to
 * the server beyond cache keying. Skipped for the render view (Bearer-token
 * auth; the response is answered as authenticated and never cached) and for
 * the external `/v1` API. On cross-site preview deploys `document.cookie`
 * cannot see the expiry cookie, so no claim is sent there — same limitation
 * as the proactive refresh above.
 */
function withAuthClaim(url: string, method: string, apiTarget: ApiTarget, hasRenderViewToken: boolean): string {
  if (method !== 'GET' || apiTarget !== ApiTarget.SERVER || hasRenderViewToken) {
    return url;
  }
  if (getAccessTokenExpiryMs() === null) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${AUTH_CLAIM_QUERY_PARAM}=${AUTH_CLAIM_QUERY_VALUE}`;
}

async function fetchAndLog(url: string, method: string, headers: HeadersInit, body?: string): Promise<Response> {
  try {
    const response = await fetch(url, {
      method,
      headers,
      body,
      // `credentials: 'include'` sends the `up-access` / `up-refresh`
      // HttpOnly cookies on every internal API call. For this to work on
      // cross-site preview deploys (UI served from `<hash>.uprightdb.com`,
      // API on `uprightplatform.com`), the cookies are issued with
      // `SameSite=None; Secure` (see `auth-cookies.ts`). CORS is a
      // separate concern: the server-side fixed allow-list with
      // `credentials: true` (`cors-origins.ts`) is what lets the browser
      // expose the credentialed *response* to the SPA, but it does not
      // attach the cookie to the request — `SameSite` does.
      credentials: 'include',
    });
    logErrorIfBadRequest(response.status, method, url, body); // Do not await
    return response;
  } catch (error) {
    logNetworkError(error, method, url, body);
    throw error;
  }
}

// Module-level promise to ensure only one refresh is in-flight at a time (mutex).
// Concurrent callers will await the same promise instead of triggering duplicate refreshes.
let refreshPromise: Promise<void> | null = null;

/**
 * Trigger a single in-flight refresh of the `up-access` cookie via
 * `POST /api/refresh-access-token`. The browser carries the `up-refresh`
 * cookie automatically; on success the server emits a fresh `up-access`
 * `Set-Cookie` header that the browser stores transparently. The promise
 * resolves with no value because the new JWT is not visible to JavaScript.
 *
 * Concurrent callers share a single in-flight request — previously a race
 * here caused paywall flickers for authenticated users (the original
 * mutex was added for exactly that bug, retained here).
 *
 * @throws TokenExpiredError when the refresh call fails (server returns
 *   non-2xx). Callers redirect to `/login` on that signal.
 */
export async function refreshAccessToken(): Promise<void> {
  if (refreshPromise) {
    await refreshPromise;
    return;
  }

  const promise = doRefreshAccessToken();
  refreshPromise = promise;

  try {
    await promise;
    return;
  } finally {
    refreshPromise = null;
  }
}

/**
 * Refresh the access cookie just before it expires — the proactive half of
 * the auth-refresh strategy (the 401-retry in `buildRequestAndFetch` is the
 * fallback half).
 *
 * The server maintains a JS-readable `up-access-expiry` companion cookie
 * (epoch ms, written atomically with `up-access` in `auth-cookies.ts`)
 * because the HttpOnly JWT itself cannot be decoded pre-flight anymore.
 * When the expiry is absent (anonymous visitor, or cookies dropped by the
 * browser) this is a no-op; when the refresh fails (refresh cookie expired
 * or revoked) we back off and let the request proceed — it will get the
 * server's honest answer (401 or anonymous) and the caller decides.
 */
async function refreshAccessTokenIfExpiringSoon(): Promise<void> {
  const expiryMs = getAccessTokenExpiryMs();
  if (expiryMs === null || Date.now() < expiryMs - ACCESS_TOKEN_EXPIRY_MARGIN_MS) {
    return;
  }
  // Back-off after a failed refresh: without this, a dead refresh cookie
  // would trigger a doomed refresh attempt on every single request.
  if (Date.now() - lastProactiveRefreshFailureAt < PROACTIVE_REFRESH_FAILURE_BACKOFF_MS) {
    return;
  }
  // Clock-skew guard: right after a *successful* refresh the token is
  // certainly fresh, even if a client clock running far ahead of the
  // server makes the expiry cookie still look past-due. Without this, such
  // a clock would re-refresh on every request.
  if (Date.now() - lastProactiveRefreshSuccessAt < PROACTIVE_REFRESH_SUCCESS_COOLDOWN_MS) {
    return;
  }
  // No attempt-level dedup needed here: concurrent callers (e.g. React
  // Query firing a burst of queries on navigation) all funnel into
  // `refreshAccessToken`'s mutex and share one in-flight request, so every
  // request in the burst waits for the fresh cookie before going out.
  try {
    await refreshAccessToken();
    // eslint-disable-next-line require-atomic-updates -- fresh Date.now(), not derived from a stale read
    lastProactiveRefreshSuccessAt = Date.now();
  } catch {
    // eslint-disable-next-line require-atomic-updates -- fresh Date.now(), not derived from a stale read
    lastProactiveRefreshFailureAt = Date.now();
    // Swallow: the request proceeds and the regular 401 / anonymous
    // handling applies. No console noise here — doRefreshAccessToken
    // already logs the failure once.
  }
}

/** Refresh this long before the nominal expiry, to absorb small client/server clock skew and request latency. */
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 30_000;
const PROACTIVE_REFRESH_FAILURE_BACKOFF_MS = 60_000;
const PROACTIVE_REFRESH_SUCCESS_COOLDOWN_MS = 30_000;
let lastProactiveRefreshFailureAt = 0;
let lastProactiveRefreshSuccessAt = 0;

/**
 * Read the access-token expiry (epoch ms) from the `up-access-expiry`
 * cookie, or `null` when unavailable. `try/catch` because `document.cookie`
 * can throw in sandboxed iframes (cf. `readLegacyRefreshToken`).
 *
 * Known limitation: the cookie belongs to the API host, so `document.cookie`
 * only exposes it when the SPA is served from the same site — true for
 * production / staging / local dev (cookies are not port-scoped), but NOT
 * for preview deploys (`<hash>.uprightdb.com` UI vs `uprightplatform.com`
 * API). There this returns `null` and the proactive refresh no-ops; the
 * refresh-on-401 fallback applies, same as before this mechanism existed.
 */
function getAccessTokenExpiryMs(): number | null {
  try {
    if (typeof document === 'undefined') return null;
    const name = sessionKind === 'eevo' ? EEVO_ACCESS_EXPIRY_COOKIE_NAME : AUTH_ACCESS_EXPIRY_COOKIE_NAME;
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
    if (!match) return null;
    const expiry = Number(match[1]);
    return Number.isFinite(expiry) ? expiry : null;
  } catch {
    return null;
  }
}

/**
 * Probe whether the current browser session is still valid by hitting an
 * authenticated endpoint that is cheap on the backend. Returns `true` on a
 * 2xx, `false` on a 401 (after one refresh attempt). Other errors propagate
 * so callers can decide whether to retry.
 *
 * Used by long-running flows (chat, leena2 stream, AI test workbench) that
 * used to read the JWT directly via `getOrRefreshAccessToken()` to decide
 * whether to redirect the user to `/login` before starting. With the
 * PLT-4720 move to HttpOnly cookies the JWT is no longer visible to JS, so
 * we ask the server instead.
 */
export async function isAuthenticatedSession(): Promise<boolean> {
  const url = buildUrl('/own-info', ApiTarget.SERVER);
  // Same proactive refresh as `buildRequestAndFetch` — avoids a guaranteed
  // 401 round-trip when the access cookie has merely lapsed.
  await refreshAccessTokenIfExpiringSoon();
  let response = await fetchAndLog(url, 'GET', { 'Content-Type': 'application/json' });
  if (response.status === 401) {
    try {
      await refreshAccessToken();
    } catch {
      return false;
    }
    response = await fetchAndLog(url, 'GET', { 'Content-Type': 'application/json' });
  }
  return response.ok;
}

async function doRefreshAccessToken(): Promise<void> {
  if (sessionKind === 'eevo') {
    const response = await fetchAndLog(buildUrl('/arvo/auth/refresh', ApiTarget.SERVER), 'POST', {
      'Content-Type': 'application/json',
    });
    if (!response.ok) throw new TokenExpiredError('Failed to refresh the Eevo session');
    return;
  }
  const url = buildUrl('/refresh-access-token', ApiTarget.SERVER);
  // PLT-4720 migration aid: if the page was loaded against an older session
  // where the JWTs still live in `localStorage` and no cookie has been set
  // yet, hand the refresh token off in the body once. The server's new
  // handler sets both cookies in the response so subsequent refreshes can
  // run cookie-only and the SPA-side `use-load-own-info` migration code
  // clears the legacy keys.
  const legacyRefreshToken = readLegacyRefreshToken();
  const body = legacyRefreshToken ? JSON.stringify({ refreshToken: legacyRefreshToken }) : '{}';

  const response = await fetchAndLog(url, 'POST', { 'Content-Type': 'application/json' }, body);

  if (!response.ok) {
    console.error('Failed to refresh access token');
    throw new TokenExpiredError('Failed to refresh access token');
  }
  // Body still includes the access token for one release of backward
  // compatibility, but the new frontend ignores it — the cookie set by the
  // server response is the authoritative store now.
}

/**
 * Read the legacy localStorage refresh token if present. Used only during
 * the PLT-4720 migration window so we can re-issue it as a cookie on the
 * first refresh after the new frontend loads. Inlined here rather than
 * imported from `storage.ts` so the lookup is `try/catch`-safe in pages
 * where `localStorage` may be unavailable (sandboxed iframes, etc.).
 */
function readLegacyRefreshToken(): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem('UPRIGHT/DEBUGGER/REFRESH_TOKEN');
  } catch {
    return null;
  }
}

function getApiUrlBase(apiTarget: ApiTarget) {
  const apiPath = (() => {
    switch (apiTarget) {
      case ApiTarget.SERVER:
        return 'api';
      case ApiTarget.PUBLIC_API_V1:
        return 'v1';
    }
  })();
  return `${getServerBaseUrl()}/${apiPath}`;
}

function buildUrl(path: string, apiTarget: ApiTarget): string {
  return `${getApiUrlBase(apiTarget)}${path}`;
}

function buildBody(bodyContent?: any): string | undefined {
  if (bodyContent) {
    return JSON.stringify(bodyContent);
  } else {
    return undefined;
  }
}
